/**
 * MockDetector: Project Health Score Calculator
 * 
 * Calculates an overall project authenticity score (0-100) based on detected violations.
 * Higher score = more authentic/production-ready.
 */

import { ScanResult } from '../types/mockdetector.types';

export interface ProjectHealthScore {
  overall: number; // 0-100
  grade: 'A+' | 'A' | 'B' | 'C' | 'D' | 'F';
  categories: {
    frontend: { score: number; violations: number };
    backend: { score: number; violations: number };
    database: { score: number; violations: number };
  };
  trend?: 'improving' | 'stable' | 'declining';
  recommendations: string[];
  criticalIssues: number;
  highIssues: number;
  mediumIssues: number;
  lowIssues: number;
}

/**
 * Calculate project health score from scan results
 */
export const calculateHealthScore = (
  scanResult: ScanResult,
  previousScore?: number
): ProjectHealthScore => {
  const violations = scanResult.mockDataViolations;
  
  // Count violations by severity
  const criticalIssues = violations.filter(v => v.severity === 'critical').length;
  const highIssues = violations.filter(v => v.severity === 'high').length;
  const mediumIssues = violations.filter(v => v.severity === 'medium').length;
  const lowIssues = violations.filter(v => v.severity === 'low').length;
  
  // Categorize violations
  const frontendViolations = violations.filter(v => {
    const desc = v.description.toLowerCase();
    return !v.file?.includes('supabase/functions') && 
           !v.file?.includes('database') &&
           (desc.includes('ui') || desc.includes('component') || desc.includes('jsx') || desc.includes('tsx'));
  });
  
  const backendViolations = violations.filter(v => {
    const desc = v.description.toLowerCase();
    return v.file?.includes('supabase/functions') ||
           desc.includes('edge function') || desc.includes('api') || desc.includes('function');
  });
  
  const databaseViolations = violations.filter(v => {
    const desc = v.description.toLowerCase();
    return v.file?.includes('database') ||
           desc.includes('database') || desc.includes('table') || desc.includes('supabase');
  });
  
  // Calculate weighted deductions
  const criticalDeduction = criticalIssues * 15;
  const highDeduction = highIssues * 8;
  const mediumDeduction = mediumIssues * 3;
  const lowDeduction = lowIssues * 1;
  
  const totalDeduction = criticalDeduction + highDeduction + mediumDeduction + lowDeduction;
  
  // Overall score (start at 100, deduct points)
  const overall = Math.max(0, Math.min(100, 100 - totalDeduction));
  
  // Category scores
  const frontendScore = Math.max(0, 100 - (frontendViolations.length * 5));
  const backendScore = Math.max(0, 100 - (backendViolations.length * 8));
  const databaseScore = Math.max(0, 100 - (databaseViolations.length * 10));
  
  // Determine grade
  const grade = getGrade(overall);
  
  // Determine trend (if previous score available)
  const trend = previousScore 
    ? overall > previousScore + 5 ? 'improving'
      : overall < previousScore - 5 ? 'declining'
      : 'stable'
    : undefined;
  
  // Generate recommendations
  const recommendations = generateRecommendations({
    criticalIssues,
    highIssues,
    mediumIssues,
    lowIssues,
    frontendViolations: frontendViolations.length,
    backendViolations: backendViolations.length,
    databaseViolations: databaseViolations.length,
    overall
  });
  
  return {
    overall: Math.round(overall),
    grade,
    categories: {
      frontend: { score: Math.round(frontendScore), violations: frontendViolations.length },
      backend: { score: Math.round(backendScore), violations: backendViolations.length },
      database: { score: Math.round(databaseScore), violations: databaseViolations.length }
    },
    trend,
    recommendations,
    criticalIssues,
    highIssues,
    mediumIssues,
    lowIssues
  };
};

/**
 * Convert numerical score to letter grade
 */
const getGrade = (score: number): 'A+' | 'A' | 'B' | 'C' | 'D' | 'F' => {
  if (score >= 98) return 'A+';
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
};

/**
 * Generate actionable recommendations based on violations
 */
const generateRecommendations = (metrics: {
  criticalIssues: number;
  highIssues: number;
  mediumIssues: number;
  lowIssues: number;
  frontendViolations: number;
  backendViolations: number;
  databaseViolations: number;
  overall: number;
}): string[] => {
  const recommendations: string[] = [];
  
  // Critical issues
  if (metrics.criticalIssues > 0) {
    recommendations.push(`🚨 URGENT: Fix ${metrics.criticalIssues} critical issue${metrics.criticalIssues > 1 ? 's' : ''} immediately (Array.fill, Math.random, hardcoded API responses)`);
  }
  
  // High priority issues
  if (metrics.highIssues > 5) {
    recommendations.push(`⚠️ HIGH PRIORITY: Address ${metrics.highIssues} high-severity mock data violations`);
  }
  
  // Database issues
  if (metrics.databaseViolations > 0) {
    recommendations.push(`💾 Database: Review ${metrics.databaseViolations} database mock data issue${metrics.databaseViolations > 1 ? 's' : ''} - ensure all queries fetch real data`);
  }
  
  // Backend issues
  if (metrics.backendViolations > 0) {
    recommendations.push(`🔧 Backend: Fix ${metrics.backendViolations} edge function mock data issue${metrics.backendViolations > 1 ? 's' : ''} - replace with real API calls`);
  }
  
  // Frontend issues
  if (metrics.frontendViolations > 5) {
    recommendations.push(`🎨 Frontend: Replace ${metrics.frontendViolations} hardcoded UI elements with dynamic data`);
  }
  
  // General recommendations based on score
  if (metrics.overall < 50) {
    recommendations.push('📊 Your project has significant mock data. Consider a systematic refactor to use real database queries.');
  } else if (metrics.overall < 80) {
    recommendations.push('✨ Good progress! Focus on remaining high-priority issues to reach production-ready status.');
  } else if (metrics.overall >= 95) {
    recommendations.push('🎉 Excellent! Your project is production-ready with minimal mock data.');
  }
  
  // Medium priority reminder
  if (metrics.mediumIssues > 10) {
    recommendations.push(`📝 Medium Priority: ${metrics.mediumIssues} issues remaining - tackle these after critical/high issues`);
  }
  
  return recommendations.slice(0, 5); // Max 5 recommendations for better UX
};

/**
 * Get color coding for health score
 */
export const getScoreColor = (score: number): string => {
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-yellow-600';
  if (score >= 50) return 'text-orange-600';
  return 'text-red-600';
};

/**
 * Get emoji for health score
 */
export const getScoreEmoji = (score: number): string => {
  if (score >= 95) return '🏆';
  if (score >= 90) return '✨';
  if (score >= 80) return '👍';
  if (score >= 70) return '😐';
  if (score >= 50) return '⚠️';
  return '🚨';
};

/**
 * Format health score for display
 */
export const formatHealthScore = (healthScore: ProjectHealthScore): string => {
  const emoji = getScoreEmoji(healthScore.overall);
  return `${emoji} ${healthScore.overall}/100 (Grade: ${healthScore.grade})`;
};

/**
 * Calculate improvement needed to reach next grade
 */
export const getImprovementNeeded = (currentScore: number): { 
  nextGrade: string; 
  pointsNeeded: number;
  violationsToFix: number;
} => {
  let nextGrade: string;
  let targetScore: number;
  
  if (currentScore >= 98) {
    return { nextGrade: 'A+', pointsNeeded: 0, violationsToFix: 0 };
  } else if (currentScore >= 90) {
    nextGrade = 'A+';
    targetScore = 98;
  } else if (currentScore >= 80) {
    nextGrade = 'A';
    targetScore = 90;
  } else if (currentScore >= 70) {
    nextGrade = 'B';
    targetScore = 80;
  } else if (currentScore >= 60) {
    nextGrade = 'C';
    targetScore = 70;
  } else {
    nextGrade = 'D';
    targetScore = 60;
  }
  
  const pointsNeeded = targetScore - currentScore;
  
  // Estimate violations to fix (assuming average 5 points per fix)
  const violationsToFix = Math.ceil(pointsNeeded / 5);
  
  return { nextGrade, pointsNeeded, violationsToFix };
};
